import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Atom, Sparkles, Zap } from "lucide-react";
import { AI_MODEL_OPTIONS, isBlankModel } from './aiModelSettings';

export { AI_MODEL_OPTIONS } from './aiModelSettings';
const modelIcons = { flash: Zap, preview: Sparkles, argon: Atom };

interface AiModelCardProps {
  enabled: boolean;
  model: string | null;
  onEnabledChange: (checked: boolean) => void;
  onModelChange: (model: string) => void;
  disabled?: boolean;
  isMobile?: boolean;
}

export const AiModelCard = ({ enabled, model, onEnabledChange, onModelChange, disabled, isMobile }: AiModelCardProps) => {
  // Show the real saved value so an empty model is visible rather than masked.
  const selected = isBlankModel(model) ? "" : model!.trim();
  const options = AI_MODEL_OPTIONS.some(option => option.value === selected)
    ? AI_MODEL_OPTIONS
    : selected ? [...AI_MODEL_OPTIONS, { value: selected, label: selected, description: 'Saved model', icon: 'preview' as const }] : AI_MODEL_OPTIONS;
  return (
    <Card className="border bg-muted/50">
      <CardContent className="py-4">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-4 w-4 text-brand-500" />
          <span className="text-sm font-semibold">AI Model</span>
        </div>
        <div className={`grid gap-4 items-end ${isMobile ? "grid-cols-1" : "grid-cols-2"}`}>
          <div className="flex items-center gap-3">
            <Switch aria-label="Enable AI" checked={enabled} onCheckedChange={onEnabledChange} disabled={disabled} />
            <div className="space-y-0.5">
              <Label className="text-sm">Enable AI</Label>
              <p className="text-xs text-muted-foreground">Analyse images with a Gemini model</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Model</Label>
            <Select value={selected} onValueChange={onModelChange} disabled={disabled || !enabled}>
              <SelectTrigger aria-label="AI model" className="w-full h-auto min-h-10 text-left">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {options.map(option => {
                  const Icon = modelIcons[option.icon];
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-brand-500 shrink-0" aria-hidden="true" />
                        <div className="flex flex-col">
                          <span className="font-medium">{option.label}</span>
                          <span className="text-xs text-muted-foreground">{option.description}</span>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
